import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { Search, ChevronDown, User, Loader2, X } from "lucide-react";
import { Button } from "./Button";
import { Input } from "./input";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getAllUsers } from "../../redux/Actions/AdminAction";
import { RoleEnum } from "../../modules";

interface User {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role?: {
        id: string;
        name: string;
    };
}

interface UserDropdownProps {
    selectedUser?: User | null;
    selectedUsers?: User[];
    onUserSelect: (user: User | null) => void;
    onUsersSelect?: (users: User[]) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    roles?: Array<{ id: number }>;
    multiSelect?: boolean;
}

const UserDropdown: React.FC<UserDropdownProps> = memo(({
    selectedUser,
    selectedUsers = [],
    onUserSelect,
    onUsersSelect,
    placeholder = "Select a user...",
    className = "",
    disabled = false,
    roles = [],
    multiSelect = false,
}) => {
    const dispatch = useAppDispatch();
    const { allUsers, loading } = useAppSelector((state: any) => state.admin || {
        allUsers: {
            data: [],
            totalCount: 0,
            hasNextPage: false,
            currentPage: 1,
            totalPages: 1,
        },
        loading: false,
    });

    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const hasLoadedUsersRef = useRef(false);
    
    // Create a cache key based on roles to track if users for this role combination have been loaded
    const rolesKey = roles.map(r => r.id).sort().join(",");
    const cacheKey = `users_${rolesKey || "all"}`;
    const hasLoadedForRolesRef = useRef<string | null>(null);

    // Load users on component mount and when roles change
    useEffect(() => {
        // Always fetch users when roles change or component mounts
        if (hasLoadedForRolesRef.current !== cacheKey && !loading) {
            hasLoadedForRolesRef.current = cacheKey;
            hasLoadedUsersRef.current = true;
            dispatch(getAllUsers({
                page: 1,
                limit: 20,
                search: "",
                roles: roles.length > 0 ? roles : undefined,
            }));
        }
    }, [dispatch, roles, loading, cacheKey]);

    // Handle search with debounce
    const handleSearch = useCallback((query: string) => {
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeout = setTimeout(() => {
            // Always make API call when search query changes, even if empty
            dispatch(getAllUsers({
                page: 1,
                limit: 20,
                search: query,
                roles: roles.length > 0 ? roles : undefined,
            }));
        }, 300);

        setSearchTimeout(timeout);
    }, [dispatch, searchTimeout, roles]);

    // Load more users
    const loadMoreUsers = useCallback(() => {
        if (!loading && allUsers.hasNextPage) {
            dispatch(getAllUsers({
                page: allUsers.currentPage + 1,
                limit: 20,
                search: searchQuery,
                roles: roles.length > 0 ? roles : undefined,
            }));
        }
    }, [dispatch, loading, allUsers.hasNextPage, allUsers.currentPage, searchQuery, roles]);

    // Handle user selection
    const handleUserSelect = (user: User) => {
        if (multiSelect) {
            const isSelected = selectedUsers.some(u => u.id === user.id);
            if (isSelected) {
                // Remove user if already selected
                const updatedUsers = selectedUsers.filter(u => u.id !== user.id);
                onUsersSelect?.(updatedUsers);
            } else {
                // Add user if not selected
                const updatedUsers = [...selectedUsers, user];
                onUsersSelect?.(updatedUsers);
            }
        } else {
            onUserSelect(user);
            setIsOpen(false);
            setSearchQuery("");
        }
    };

    // Handle clear selection
    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (multiSelect) {
            onUsersSelect?.([]);
        } else {
            onUserSelect(null);
        }
    };

    // Handle remove specific user (for multi-select)
    const handleRemoveUser = (userToRemove: User, e: React.MouseEvent) => {
        e.stopPropagation();
        const updatedUsers = selectedUsers.filter(u => u.id !== userToRemove.id);
        onUsersSelect?.(updatedUsers);
    };

    // Handle dropdown toggle
    const toggleDropdown = () => {
        if (disabled) return;
        setIsOpen(!isOpen);
        if (!isOpen) {
            // Focus search input when opening
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    };

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    const displayName = (user: User) => {
        return `${user.firstName} ${user.lastName}`;
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Trigger Button */}
            <button
                type="button"
                onClick={toggleDropdown}
                disabled={disabled}
                className={`
					w-full flex items-center justify-between px-3 py-2 border rounded-lg min-h-[42px]
					${disabled
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    }
					transition-colors
				`}
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    {multiSelect ? (
                        selectedUsers.length > 0 ? (
                            <div className="flex flex-wrap gap-1 flex-1">
                                {selectedUsers.map((user) => (
                                    <span
                                        key={user.id}
                                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                                    >
                                        <User className="h-3 w-3" />
                                        <span className="truncate max-w-20">{displayName(user)}</span>
                                        <button
                                            type="button"
                                            onClick={(e) => handleRemoveUser(user, e)}
                                            className="hover:bg-blue-200 rounded-full p-0.5"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <span className="text-gray-500">{placeholder}</span>
                        )
                    ) : selectedUser ? (
                        <>
                            <User className="h-4 w-4 text-gray-500 flex-shrink-0" />
                            <span className="truncate">{displayName(selectedUser)}</span>
                            <span className="text-gray-400 text-sm">({selectedUser.email})</span>
                        </>
                    ) : (
                        <span className="text-gray-500">{placeholder}</span>
                    )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {(multiSelect ? selectedUsers.length > 0 : selectedUser) && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                        >
                            <X className="h-3 w-3 text-gray-400" />
                        </button>
                    )}
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
                    {/* Search Input */}
                    <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search users..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    handleSearch(e.target.value);
                                }}
                                className="pl-10"
                            />
                        </div>
                    </div>

                    {/* Users List */}
                    <div className="max-h-60 overflow-y-auto">
                        {loading && allUsers.data.length === 0 ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                                <span className="ml-2 text-gray-500">Loading users...</span>
                            </div>
                        ) : allUsers.data.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                                <User className="h-8 w-8 text-gray-300 mb-2" />
                                <p>No users found</p>
                                {searchQuery && (
                                    <p className="text-sm text-gray-400">Try a different search term</p>
                                )}
                            </div>
                        ) : (
                            <>
                                {allUsers.data.map((user: User) => (
                                    <button
                                        key={user.id}
                                        type="button"
                                        onClick={() => handleUserSelect(user)}
                                        className={`
											w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50
											${multiSelect 
                                                ? selectedUsers.some(u => u.id === user.id) 
                                                    ? "bg-blue-50 border-l-4 border-blue-500" 
                                                    : ""
                                                : selectedUser?.id === user.id 
                                                    ? "bg-blue-50 border-l-4 border-blue-500" 
                                                    : ""
                                            }
											transition-colors
										`}
                                    >
                                        <div className="flex-shrink-0">
                                            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                                <User className="h-4 w-4 text-gray-500" />
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-900 truncate">
                                                {displayName(user)}
                                            </p>
                                            <p className="text-sm text-gray-500 truncate">{user.email}</p>
                                            {user.role && (
                                                <p className="text-xs text-gray-400">{user.role.name}</p>
                                            )}
                                        </div>
                                    </button>
                                ))}

                                {/* Load More Button */}
                                {allUsers.hasNextPage && (
                                    <div className="p-3 border-t border-gray-200">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={loadMoreUsers}
                                            disabled={loading}
                                            className="w-full flex items-center justify-center gap-2"
                                        >
                                            {loading ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    Loading more...
                                                </>
                                            ) : (
                                                <>
                                                    Load More ({allUsers.totalCount - allUsers.data.length} remaining)
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
});

UserDropdown.displayName = "UserDropdown";

export default UserDropdown;
